import React from 'react';
import { showConfirmDialog, showSaveDialog } from '../DialogHelper';
import { showAlertSnackbar } from '../SnackbarHelper';
/**
 * Provides the `NavigationInterface` context for managing navigation behavior,
 * such as handling unsaved changes, auto-save flows, and confirmation dialogs.
 *
 * @param {React.PropsWithChildren} props - React props.
 * @param {React.ReactNode} props.children - Child components to render within the provider.
 * @returns {JSX.Element} A context provider component for navigation utilities.
 */
export const NavigationContextProvider = (props) => {
    const changedRef = React.useRef(undefined);
    const addBeforeUnloadListener = () => {
        const beforeUnload = (event) => {
            // note that it is not possible to customize the message
            const message = 'Changes you made may not be saved.';
            event.returnValue = message;
            return message;
        };
        window.addEventListener('beforeunload', beforeUnload);
        return beforeUnload;
    };
    const addPopStateListener = () => {
        const popState = () => {
            // unfortunately we cannot do anything useful here, like show a confirmation
            clearChanged();
        };
        window.addEventListener('popstate', popState);
        return popState;
    };
    const removeBeforeUnloadListener = () => {
        if (changedRef.current && changedRef.current.beforeUnload) {
            window.removeEventListener('beforeunload', changedRef.current.beforeUnload);
            changedRef.current.beforeUnload = undefined;
        }
    };
    const removePopStateListener = () => {
        if (changedRef.current && changedRef.current.popState) {
            window.removeEventListener('popstate', changedRef.current.popState);
            changedRef.current.popState = undefined;
        }
    };
    const clearChanged = () => {
        removeBeforeUnloadListener();
        removePopStateListener();
        changedRef.current = undefined;
    };
    const value = {
        navigate: (navigateCallback, reloadAfterSave) => {
            var _a;
            const changed = changedRef.current;
            if (changed) {
                const { autoSaveCallback, message, saveCallback } = changed;
                if (changed.savePromise) {
                    // New promised base flow, save is in progress
                    // Record the new navigation and show a warning in a snackbar
                    changed.afterSaveActions = {
                        navigate: navigateCallback,
                        reload: reloadAfterSave,
                    };
                    showAlertSnackbar({
                        message: 'Please wait while processing your request',
                        type: 'warning',
                        autoHideDuration: 3000,
                    });
                }
                else if (autoSaveCallback) {
                    // New promise base auto-save flow
                    changed.savePromise = autoSaveCallback();
                    if ((_a = changed.autoSaveOptions) === null || _a === void 0 ? void 0 : _a.cancelNavigationOnError) {
                        changed.savePromise
                            .then(() => {
                            clearChanged();
                            navigateCallback();
                        })
                            .catch(() => {
                            // It is up for the component calling this function to show errors to the user.
                            changed.savePromise = undefined;
                        });
                    }
                    else {
                        changed.savePromise
                            .then(() => {
                            clearChanged();
                            navigateCallback();
                        })
                            .catch(() => {
                            // It is up for the component calling this function to show errors to the user.
                            clearChanged();
                            navigateCallback();
                        });
                    }
                }
                else if (saveCallback) {
                    showSaveDialog({
                        title: message !== null && message !== void 0 ? message : 'Save changes?',
                        onSave: () => {
                            changed.afterSaveActions = {
                                navigate: navigateCallback,
                                reload: reloadAfterSave,
                            };
                            const saveResponse = saveCallback(changed.afterSaveActions);
                            if (saveResponse) {
                                // New promised base flow
                                // With this flow components no longer need to call clearChanged
                                // If the user tries more than one navigation while save is in progress a warning will be shown in a snackbar
                                changed.savePromise = saveResponse;
                                changed.savePromise
                                    .then(() => {
                                    var _a, _b;
                                    clearChanged();
                                    (_b = (_a = changed.afterSaveActions) === null || _a === void 0 ? void 0 : _a.navigate) === null || _b === void 0 ? void 0 : _b.call(_a);
                                })
                                    .catch(() => {
                                    // It is up for the component calling this function to show errors to the user.
                                    changed.savePromise = undefined;
                                });
                            }
                            else {
                                // Deprecated legacy flow
                                // With this flow there is no way to detect multiple navigations while save is in progress
                                changed.savePromise = undefined;
                            }
                        },
                        onDontSave: () => {
                            clearChanged();
                            navigateCallback();
                        },
                        onCancel: noop,
                    });
                }
                else {
                    showConfirmDialog({
                        title: message !== null && message !== void 0 ? message : 'Changes you made may not be saved.',
                        onConfirm: () => {
                            clearChanged();
                            navigateCallback();
                        },
                        onCancel: noop,
                    });
                }
            }
            else {
                navigateCallback();
            }
        },
        save: (savePromise) => {
            var _a;
            // New promised base flow
            // With this flow components need to call save and pass a promise
            // If the user tries more than one navigation while save is in progress a warning will be shown in a snackbar
            if (!changedRef.current) {
                changedRef.current = {};
            }
            const changed = changedRef.current;
            changed.savePromise = savePromise;
            if (changed.autoSaveCallback == null || ((_a = changed === null || changed === void 0 ? void 0 : changed.autoSaveOptions) === null || _a === void 0 ? void 0 : _a.cancelNavigationOnError)) {
                changed.savePromise
                    .then(() => {
                    var _a, _b;
                    clearChanged();
                    (_b = (_a = changed.afterSaveActions) === null || _a === void 0 ? void 0 : _a.navigate) === null || _b === void 0 ? void 0 : _b.call(_a);
                })
                    .catch(() => {
                    // It is up for the component calling this function to show errors to the user.
                    changed.savePromise = undefined;
                });
            }
            else {
                changed.savePromise
                    .then(() => {
                    var _a;
                    clearChanged();
                    if ((_a = changed.afterSaveActions) === null || _a === void 0 ? void 0 : _a.navigate) {
                        changed.afterSaveActions.navigate();
                    }
                })
                    .catch(() => {
                    var _a;
                    // It is up for the component calling this function to show errors to the user.
                    changed.savePromise = undefined;
                    if ((_a = changed.afterSaveActions) === null || _a === void 0 ? void 0 : _a.navigate) {
                        clearChanged();
                        changed.afterSaveActions.navigate();
                    }
                });
            }
        },
        setAutoSave: (autoSaveCallback, options) => {
            removeBeforeUnloadListener();
            removePopStateListener();
            changedRef.current = {
                beforeUnload: addBeforeUnloadListener(),
                popState: addPopStateListener(),
                autoSaveCallback,
                autoSaveOptions: options,
            };
        },
        setChanged: (message, saveCallback) => {
            removeBeforeUnloadListener();
            removePopStateListener();
            changedRef.current = {
                beforeUnload: addBeforeUnloadListener(),
                popState: addPopStateListener(),
                message,
                saveCallback,
            };
        },
        clearChanged: clearChanged,
        cleanUp: () => {
            if (changedRef.current) {
                console.warn('Detected broken navigation code, some component that tracks unsaved changes was destroyed with unsaved changes. Most likely some navigation code is not wrapped in NavigationContext.navigate().');
                clearChanged();
            }
        },
        afterSave: (afterSaveCallback) => {
            var _a;
            try {
                afterSaveCallback((_a = changedRef.current) === null || _a === void 0 ? void 0 : _a.afterSaveActions);
            }
            finally {
                clearChanged();
            }
        },
        getAfterSaveActions: () => { var _a, _b; return (_b = (_a = changedRef.current) === null || _a === void 0 ? void 0 : _a.afterSaveActions) !== null && _b !== void 0 ? _b : {}; },
    };
    return React.createElement(NavigationContext.Provider, { value: value }, props.children);
};
const noop = () => undefined;
/**
 * React context that implements the {@link NavigationInterface}}.
 */
export const NavigationContext = React.createContext({
    navigate: noop,
    save: noop,
    setAutoSave: noop,
    setChanged: noop,
    clearChanged: noop,
    cleanUp: noop,
    afterSave: noop,
    getAfterSaveActions: () => ({}),
});
